import React from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import classnames from 'classnames'

import Seo from '../Seo/Seo'
import Hero from '../Hero/Hero'
import RichText from '../RichText/RichText'
import List from '../List/List'
import Quote from '../Quote/Quote'
import InfoList from '../InfoList/InfoList'
import PageFooter from '../PageFooter/PageFooter'
import ParallaxBackground from '../ParallaxBackground/ParallaxBackground'
import Breakpoint from '../Breakpoint/Breakpoint'
import Button from '../Button/Button'
import Gallery from '../Gallery/Gallery'
import Partner from '../Partner/Partner'

import getContentType from '../../utils/getContentType'
import { setGlobalState } from '../../utils/globalState'

import {
  section,
  container,
  containerWide,
  textBlock,
  imageText,
  imageTextImg,
  imageTextText,
} from './PageContent.module.scss'

const PageContent = ({ pageId, className, ...props }) => {
  const data = useStaticQuery(graphql`
    {
      wp {
        pages {
          nodes {
            databaseId
            title
            featuredImage {
              node {
                sourceUrl
                srcSet
                altText
                mediaDetails {
                  width
                  height
                }
              }
            }
            seo {
              title
            }
            servicePageBuilder {
              contenteditor {
                ... on WPGraphQL_Page_Servicepagebuilder_Contenteditor_Text {
                  fieldGroupName
                  text
                }
                ... on WPGraphQL_Page_Servicepagebuilder_Contenteditor_ImageText {
                  fieldGroupName
                  img {
                    sourceUrl
                    srcSet
                    altText
                    mediaDetails {
                      width
                      height
                    }
                  }
                  text
                }
                ... on WPGraphQL_Page_Servicepagebuilder_Contenteditor_List {
                  fieldGroupName
                  columns
                  list {
                    item
                  }
                }
                ... on WPGraphQL_Page_Servicepagebuilder_Contenteditor_InfoList {
                  fieldGroupName
                  columns
                  background
                  type
                  infoList {
                    text
                    icon {
                      sourceUrl
                      mediaDetails {
                        width
                        height
                      }
                      altText
                    }
                  }
                }
                ... on WPGraphQL_Page_Servicepagebuilder_Contenteditor_Quote {
                  fieldGroupName
                  text
                }
                ... on WPGraphQL_Page_Servicepagebuilder_Contenteditor_Button {
                  fieldGroupName
                  color
                  hollow
                  text
                  popupTitle
                }
                ... on WPGraphQL_Page_Servicepagebuilder_Contenteditor_Gallery {
                  fieldGroupName
                  size
                  description
                  images {
                    id
                    altText
                    sourceUrl
                    srcSet(size: LARGE)
                    mediaDetails {
                      sizes {
                        name
                        sourceUrl
                        width
                        height
                      }
                    }
                  }
                }
                ... on WPGraphQL_Page_Servicepagebuilder_Contenteditor_Partner {
                  fieldGroupName
                  logo1 {
                    sourceUrl
                    altText
                    mediaDetails {
                      width
                      height
                    }
                  }
                  logo2 {
                    sourceUrl
                    altText
                    mediaDetails {
                      width
                      height
                    }
                  }
                  text
                  link {
                    title
                    url
                  }
                }
                ... on WPGraphQL_Page_Servicepagebuilder_Contenteditor_Footer {
                  fieldGroupName
                  img {
                    sourceUrl
                    srcSet
                    altText
                    mediaDetails {
                      width
                      height
                    }
                  }
                  title
                  footerList {
                    size
                    items {
                      text
                    }
                  }
                  description
                }
              }
            }
          }
        }
      }
    }
  `)

  const pageData = data.wp.pages.nodes.filter(
    (pageData) => pageData.databaseId === pageId
  )[0]

  const openModal = () => {
    setGlobalState('showGetStart', true)
  }

  const { title, seo, featuredImage } = pageData
  const { contenteditor } = pageData.servicePageBuilder

  const Container = ({ wide, children, ...props }) => {
    return (
      <div className={classnames(container, wide && containerWide)} {...props}>
        {children}
      </div>
    )
  }

  const blocs = contenteditor.map((content, idx) => {
    const contentType = getContentType(content.fieldGroupName)
    const wideContent = ['gallery']

    return (
      <Container key={idx} wide={wideContent.includes(contentType)}>
        {contentType === 'text' && (
          <RichText className={textBlock}>{content.text}</RichText>
        )}
        {contentType === 'imagetext' && (
          <div className={imageText}>
            <RichText className={classnames(imageTextText, textBlock)}>
              {content.text}
            </RichText>
            <div className={imageTextImg}>
              <img
                src={content.img.sourceUrl}
                srcSet={content.img.srcSet}
                alt={content.img.altText}
                width={content.img.mediaDetails.width}
                height={content.img.mediaDetails.height}
              />
            </div>
          </div>
        )}
        {contentType === 'list' && (
          <List
            items={content.list.map((item) => item.item)}
            columns={content.columns}
          />
        )}
        {contentType === 'quote' && <Quote>{content.text}</Quote>}
        {contentType === 'infolist' && (
          <InfoList
            items={content.infoList}
            itemType={content.type}
            columns={content.columns}
            background={content.background}
          />
        )}
        {contentType === 'gallery' && <Gallery {...content} />}
        {contentType === 'button' && (
          <Button
            hollow={content.hollow}
            secondary={content.color === 'secondary'}
            onClick={openModal}
          >
            {content.text}
          </Button>
        )}
        {contentType === 'partner' && <Partner {...content} />}
        {contentType === 'footer' && <PageFooter data={content} />}
      </Container>
    )
  })

  return (
    <>
      <Seo wpSeo={seo} />
      <Hero title={title} featuredImage={featuredImage.node} />
      <div className={classnames(section, className)} {...props}>
        {blocs}
        <Breakpoint showFor={'xlarge'}>
          <ParallaxBackground />
        </Breakpoint>
      </div>
    </>
  )
}

export default PageContent
